import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Shimmer } from "../../component-library/Shimmer";

const LazyImage = ({
  src,
  alt,
  width = "100%",
  height,
  aspectRatio = "var(--feature-ratio, 16/9)",
  className,
  imgClassName = "",
  style,
  shimmerProps = {}
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "200px 0px",
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (!src) {
    return (
      <Shimmer
        width={width}
        height={height}
        style={{
          aspectRatio,
          ...shimmerProps.style
        }}
        borderRadius={style?.borderRadius || "8px"}
        {...shimmerProps}
      />
    );
  }

  const containerStyle = {
    position: "relative",
    width,
    height,
    aspectRatio,
    overflow: "hidden",
    borderRadius: style?.borderRadius || "8px",
    maxHeight: "inherit",
    ...style
  };

  const wrapperStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "block"
  };

  const imageStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: isLoaded ? 1 : 0,
    transition: "opacity 0.3s ease-in-out",
    borderRadius: style?.borderRadius || "8px"
  };

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className={className}
      data-testid="lazy-image-container"
    >
      <div style={wrapperStyle}>
        <Shimmer
          width="100%"
          height="100%"
          borderRadius={style?.borderRadius || "8px"}
          {...shimmerProps}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: isLoaded ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
            ...shimmerProps.style
          }}
        />
        {shouldLoad && (
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            style={imageStyle}
            className={imgClassName}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            data-testid="lazy-image"
          />
        )}
      </div>
    </div>
  );
};

LazyImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aspectRatio: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  shimmerProps: PropTypes.object
};

export default LazyImage;
